import ExcelJS from "exceljs";
import PGSHelper from "../../helpers/PGSHelper.js";

// Cede el hilo al navegador cada cierto número de filas para que la pestaña no se congele
const ROWS_PER_CHUNK = 500;
const yieldToBrowser = () => new Promise((resolve) => setTimeout(resolve, 0));

const $pgsHelper = new PGSHelper();

export default class GenericExcelExporter {
  
  static formatValue(val, depth = 0) {
    if (val === null || val === undefined) return "";
    
    const indent = "  ".repeat(depth);

    // Caso: Arreglos
    if (Array.isArray(val)) {
      return val
        .map((item) => `${indent}- ${GenericExcelExporter.formatValue(item, depth + 1).trim()}`)
        .join("\n");
    }

    // Caso: Objetos (y no es un error o fecha)
    if (typeof val === "object" && !(val instanceof Date)) {
      return Object.entries(val)
        .map(([key, value]) => {
          const formattedValue = GenericExcelExporter.formatValue(value, depth + 1);
          // Si el valor es complejo, saltamos línea después de la llave
          const separator = (typeof value === "object" && value !== null) ? "\n" : " ";
          return `${indent}${key}:${separator}${formattedValue}`;
        })
        .join("\n");
    }

    // Caso: Valores simples
    if (typeof val === "number") {
      return Number(val);
    }

    return String(val);
  }

  static async generateWorkbook(data, options = {}) {
    const workbook = new ExcelJS.Workbook();
    const sheetName = options.sheetName || "Datos";
    const worksheet = workbook.addWorksheet(sheetName);

    // Determinar headers y mapping según si se proporcionan json_fields en options
    let headers, mapping;
    if (options.json_fields) {
      mapping = options.json_fields;
      headers = Object.keys(mapping);
    } else {
      headers = data.length > 0 ? Object.keys(data[0]) : [];
    }

    // Si se proporciona un título, agregar una fila superior con el mismo
    if (options.title) {
      // mergeCells(filaInicial, columnaInicial, filaFinal, columnaFinal)
      worksheet.mergeCells(1, 1, 1, headers.length || 1);
      
      const titleCell = worksheet.getCell("A1");
      titleCell.value = options.title;
      titleCell.font = { bold: true, size: 18 };
      titleCell.alignment = { horizontal: "center", vertical: "middle" };
      titleCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: options.titleBgColor || "FFADD8E6" }, 
      };
      
      // Deja una fila en blanco debajo del título
      worksheet.addRow([]);
    }

    // Agregar la fila de encabezados
    const headerRow = worksheet.addRow(headers);
    headerRow.font = options.headerFont || { bold: true, size: 12 };
    headerRow.alignment = options.headerAlignment || {
      horizontal: "center",
      vertical: "middle",
    };
    
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        // Se evalúa la propiedad de las opciones, con el gris claro como respaldo
        fgColor: { argb: options.headerBgColor || "FFD3D3D3" }, 
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Función auxiliar para extraer valores anidados a partir de una ruta 
    const getNestedValue = (obj, keyPath) =>
      keyPath
        .split(".")
        .reduce((acc, key) => (acc ? acc[key] : undefined), obj);

    // Agregar cada fila de datos en el mismo orden de los encabezados
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      let rowData;
      if (mapping) {
        rowData = headers.map((header) =>
          GenericExcelExporter.formatValue(getNestedValue(item, mapping[header]))
        );
      } else {
        rowData = headers.map((key) => 
          GenericExcelExporter.formatValue(item[key])
        );
      }
      
      const row = worksheet.addRow(rowData);
      row.alignment = options.rowAlignment || {
        horizontal: "left",
        vertical: "middle",
        wrapText: true, 
      };
      if ((i + 1) % ROWS_PER_CHUNK === 0) await yieldToBrowser();
    }

    // Autoajuste de columnas (optimizado)
    worksheet.columns.forEach((column) => {
      let maxLength = column.header ? column.header.toString().length : 10;
      
      // Extraemos los valores de la columna y tomamos una muestra (50 filas)
      const valuesSample = column.values.slice(1, 52); 
      
      valuesSample.forEach((val) => {
        if (val) {
          const textLength = val.toString().length;
          if (textLength > maxLength) {
            maxLength = textLength;
          }
        }
      });
      
      column.width = maxLength + 2; 
    });

    return workbook;
  }

  static async generateExcelBuffer(data, options = {}) {
    const workbook = await GenericExcelExporter.generateWorkbook(data, options);
    return await workbook.xlsx.writeBuffer();
  }
}

/**
 * Ejemplo de usos:
 * * |-> Ejemplo de arrays aceptados (¡Ahora soporta objetos anidados y arreglos!)
 * |
 * v
   const data = [
     { product: "Laptop", price: 1200, specs: { ram: "16GB", storage: "512GB SSD" }, tags: ["Oferta", "Nuevo"] },
     { product: "Smartphone", price: 800, specs: { ram: "8GB", storage: "128GB" }, tags: ["Popular"] },
     { product: "Tablet", price: 600, specs: { ram: "4GB", storage: "64GB" }, tags: [] }
   ];
   
   const myData = [
     { name: "John", age: 30, location: { city: "New York", zip: "10001" } },
     { name: "Jane", age: 25, location: { city: "Los Angeles", zip: "90001" } },
     { name: "Mark", age: 35, location: { city: "Chicago", zip: "60007" } }
   ];
 * * |-> Opciones (completamente opcional)
 * |
 * v
   const options = {
     title: "Inventario de Productos",
     sheetName: "Inventario",
     headerBgColor: "FF99CCFF", // Color de fondo personalizado para encabezados
     titleBgColor: "FFFFCC00",  // Color de fondo para el título
     headerFont: { bold: true, size: 14 },
     headerAlignment: { horizontal: "center", vertical: "middle" },
     // wrapText: true es ideal para que los objetos/arreglos anidados hagan salto de línea
     rowAlignment: { horizontal: "left", vertical: "middle", wrapText: true }, 
     json_fields: {
       "Nombre": "product",
       "Precio": "price",
       "Memoria RAM": "specs.ram",       // <-- Extracción de objeto anidado
       "Almacenamiento": "specs.storage",// <-- Extracción de objeto anidado
       "Etiquetas": "tags"               // <-- Procesará el array como viñetas automáticamente
     }
   };
 * * |-> Ejemplo para exportar de forma personalizada
 * |
 * v
   exportExcel() {
     GenericExcelExporter.generateExcelBuffer(data, options)
       .then(buffer => {
         const blob = new Blob([buffer], {
           type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
         });
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = "Inventario.xlsx";
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
       })
       .catch(err => console.error("Error exportando Excel:", err));
   },
 * * |-> Ejemplo para exportar de forma simple (Auto-mapeo de datos complejos)
 * |
 * v
   exportExcel() {
     GenericExcelExporter.generateExcelBuffer(myData)
       .then(buffer => {
         const blob = new Blob([buffer], {
           type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
         });
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = "MyData.xlsx";
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
       })
       .catch(error => console.error("Error exportando:", error));
   },
 */